import algoliasearchV4 from "algoliasearch";
import allSettled from "promise-all-settled";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import get from "lodash/get";
import currencyRanges from "./currencyRanges.json";

const flattenDestinationItems = [];

if (typeof window.zubluDestination !== "undefined") {
  for (const level1 of window.zubluDestination.children) {
    flattenDestinationItems.push(level1);

    if (Array.isArray(level1.children)) {
      for (const level2 of level1.children) {
        flattenDestinationItems.push(level2);
      }
    }
  }
}

function extractFullMatch(hits) {
  const bestHit = hits.find((hit) => {
    return hit._highlightResult.__platform_name.fullyHighlighted;
  });

  if (bestHit) {
    return bestHit;
  }

  return hits[0];
}

export const allDestinationItems = flattenDestinationItems;

export const removeHighlightHTML = (result) => {
  let output = String(result);

  output = output.replace(new RegExp("<em>", "g"), "");
  output = output.replace(new RegExp("</em>", "g"), "");

  return output;
};

export const syncURLParams = (urlSearchParams) => {
  const currentQuery = window.location.search.substr(1);
  const newQuery = urlSearchParams.toString();

  if (newQuery !== currentQuery) {
    window.history.pushState({}, "", `?${decodeURIComponent(newQuery)}`);
  }
};

export const searchWithDelay = (fn, howLong = 1000) => {
  return (args) => {
    const timerPromise = new Promise((resolve) => {
      setTimeout(resolve, howLong);
    });

    return allSettled([fn(args), timerPromise]).then((results) => {
      if (results[0].state === "fulfilled") {
        return results[0].result;
      } else {
        throw new Error("Main function failed");
      }
    });
  };
};

export const transformAlgoliaHitsToResortResult = (
  algoliaResult,
  searchType
) => {
  let results = [];
  let featuredSearches = get(
    window,
    ["topNavStore", "state", "featuredSearchStats"],
    []
  );

  for (const hit of algoliaResult.hits) {
    if (hit.__platform_weight === 3) {
      if (
        featuredSearches.some(
          (entry) =>
            entry.destination_code === hit.__platform_skuKey && entry.has_resort
        ) ||
        featuredSearches.some(
          (entry) => entry.destination_code === hit.__platform_skuKey
        ) === false
      ) {
        results.push({
          type: "destination",
          id: hit.objectID,
          title: hit.__platform_destination_title,
          subtitle: `${hit.__platform_region_title} ${hit.__platform_country_title}`,
          code: hit.__platform_skuKey,
          weight: 3,
        });
      }
    }

    if (hit.__platform_weight === 2) {
      if (
        featuredSearches.some(
          (entry) =>
            entry.destination_code === hit.__platform_skuKey && entry.has_resort
        ) ||
        featuredSearches.some(
          (entry) => entry.destination_code === hit.__platform_skuKey
        ) === false
      ) {
        results.push({
          type: "province",
          id: hit.objectID,
          title: hit.__platform_region_title,
          subtitle: hit.__platform_country_title,
          code: hit.__platform_skuKey,
          weight: 2,
        });
      }
    }

    if (hit.__platform_weight === 1) {
      if (
        featuredSearches.some(
          (entry) =>
            entry.country_code === hit.__platform_skuKey && entry.has_resort
        ) ||
        featuredSearches.some(
          (entry) => entry.country_code === hit.__platform_skuKey
        ) === false
      ) {
        results.push({
          type: "country",
          id: hit.objectID,
          title: hit.__platform_country_title,
          code: hit.__platform_skuKey,
          weight: 1,
        });
      }
    }

    if (hit._index === zubluAlgolia.resortIndex) {
      results.push({
        type: "resort",
        id: hit.objectID,
        title: hit.__platform_name,
        subtitle: `Resort in ${hit.__platform_country_title}`,
        url: hit.__platform_url,
        weight: 4,
      });
    }
  }

  results = orderBy(results, ["weight", "title"], ["asc", "asc"]);
  results = uniqBy(results, (entry) => entry.title + entry.subtitle);
  results = results.slice(0, 7);

  return results;
};

export const transformAlgoliaHitsToLiveaboardResult = (algoliaResult) => {
  let results = [];
  let featuredSearches = get(
    window,
    ["topNavStore", "state", "featuredSearchStats"],
    []
  );

  for (const hit of algoliaResult.hits) {
    if (hit.__platform_weight === 3) {
      if (
        featuredSearches.some(
          (entry) =>
            entry.destination_code === hit.__platform_skuKey &&
            entry.has_liveaboards
        ) ||
        featuredSearches.some(
          (entry) => entry.destination_code === hit.__platform_skuKey
        ) === false
      ) {
        results.push({
          type: "destination",
          id: hit.objectID,
          title: hit.__platform_destination_title,
          subtitle: `${hit.__platform_region_title}, ${hit.__platform_country_title}`,
          code: hit.__platform_skuKey,
          weight: 3,
        });
      }
    }

    if (hit.__platform_weight === 2) {
      if (
        featuredSearches.some(
          (entry) =>
            entry.destination_code === hit.__platform_skuKey &&
            entry.has_liveaboards
        ) ||
        featuredSearches.some(
          (entry) => entry.destination_code === hit.__platform_skuKey
        ) === false
      ) {
        results.push({
          type: "province",
          id: hit.objectID,
          title: hit.__platform_region_title,
          subtitle: hit.__platform_country_title,
          code: hit.__platform_skuKey,
          weight: 2,
        });
      }
    }

    if (hit.__platform_weight === 1) {
      if (
        featuredSearches.some(
          (entry) =>
            entry.country_code === hit.__platform_skuKey &&
            entry.has_liveaboards
        ) ||
        featuredSearches.some(
          (entry) => entry.country_code === hit.__platform_skuKey
        ) === false
      ) {
        results.push({
          type: "country",
          id: hit.objectID,
          title: hit.__platform_country_title,
          code: hit.__platform_skuKey,
          weight: 1,
        });
      }
    }

    if (hit._index === zubluAlgolia.liveaboardIndex) {
      results.push({
        type: "liveaboard",
        id: hit.objectID,
        title: hit.__platform_name,
        subtitle: `Liveaboard in ${hit.__platform_country_title}`,
        url: hit.__platform_url,
        weight: 0,
      });
    }
  }

  results = orderBy(results, ["weight", "title"], ["asc", "asc"]);
  results = uniqBy(results, (entry) => entry.title + entry.subtitle);
  results = results.slice(0, 7);

  return results;
};

export const transformAlgoliaHitsToEcoventureResult = (algoliaResult) => {
  let results = [];
  let featuredSearches = get(
    window,
    ["topNavStore", "state", "featuredSearchStats"],
    []
  );

  for (const hit of algoliaResult.hits) {
    const matchedCountryInfoIndex = get(
      hit,
      ["_highlightResult", "__odyssey_country_programme"],
      []
    ).findIndex((countryProgramme) => {
      if (countryProgramme.name) {
        return countryProgramme.name.matchLevel === "full";
      }

      return false;
    });

    if (matchedCountryInfoIndex !== -1) {
      const matchedCountry =
        hit.__odyssey_country_programme[matchedCountryInfoIndex];

      if (
        featuredSearches.some(
          (entry) =>
            entry.country_code === matchedCountry.code && entry.has_ecoventures
        )
      ) {
        results.push({
          type: "country",
          id: matchedCountry.code,
          title: matchedCountry.name,
          subtitle: null,
          url: `/s/ecoventures?destinations=${matchedCountry.code}`,
          code: matchedCountry.code,
          weight: 2,
        });
      }
    }

    if (
      get(
        hit,
        ["_highlightResult", "__odyssey_supplier_info", "name", "matchLevel"],
        "none"
      ) === "full"
    ) {
      if (hit.__odyssey_resort_url) {
        const destination = hit.__odyssey_destination.name;
        const country = hit.__odyssey_country.name;

        results.push({
          type: "ecoventure",
          id: hit.objectID,
          title: hit.__odyssey_supplier_info.name,
          subtitle: `${destination}, ${country}`,
          url: hit.__odyssey_resort_url,
          weight: 1,
        });
      }
    }

    if (
      get(
        hit,
        ["_highlightResult", "__odyssey_programme_name", "matchLevel"],
        "none"
      ) === "full"
    ) {
      if (hit.__odyssey_resort_url) {
        const destination = hit.__odyssey_destination.name;
        const country = hit.__odyssey_country.name;

        results.push({
          type: "ecoventure",
          id: hit.objectID,
          title: hit.__odyssey_programme_name,
          subtitle: `${destination}, ${country}`,
          url: hit.__odyssey_resort_url,
          weight: 1,
        });
      }
    }
  }

  results = uniqBy(results, (entry) => entry.id);
  results = uniqBy(results, (entry) => entry.url);
  results = orderBy(results, ["weight", "title"], ["asc", "asc"]);
  results = results.slice(0, 5);

  return results;
};

export const transformAlgoliaHitsToYachtResult = (algoliaResult) => {
  let results = [];
  let featuredSearches = get(
    window,
    ["topNavStore", "state", "featuredSearchStats"],
    []
  );

  for (const hit of algoliaResult.hits) {
    if (hit._index === zubluAlgolia.destinationIndex) {
      if (hit.__platform_weight === 1) {
        if (
          featuredSearches.some(
            (entry) =>
              entry.country_code === hit.__platform_skuKey && entry.has_yacht
          ) ||
          featuredSearches.some(
            (entry) => entry.country_code === hit.__platform_skuKey
          ) === false
        ) {
          results.push({
            type: "country",
            id: hit.objectID,
            title: hit.__platform_country_title,
            code: hit.__platform_skuKey,
            weight: 1,
          });
        }
      }
    }

    if (hit._index === zubluAlgolia.yachtIndex) {
      results.push({
        type: "yacht",
        id: hit.objectID,
        title: hit.__platform_name,
        subtitle: `Charter Yacht in ${hit.__platform_country_title}`,
        url: hit.__platform_url,
        weight: 4,
      });
    }
  }

  results = orderBy(results, ["weight", "title"], ["asc", "asc"]);
  results = uniqBy(results, (entry) => entry.title + entry.subtitle);
  results = results.slice(0, 5);

  return results;
};

export const convertToSearchURL = (destinationURL, type) => {
  const { pathname } = new URL(destinationURL);
  const newPath = pathname.replace("/destination", `/s/${type}s`);

  return newPath;
};

export const escapeQuote = (str) => {
  if (str && str.replace) {
    return str.replace(/&#039;/g, "'");
  }

  return str;
};

export const searchDestinationForEcoventure = (
  where,
  destinationIndex,
  data
) => {
  return destinationIndex
    .search(where, {
      restrictSearchableAttributes: ["__platform_name"],
      attributesToRetrieve: ["__platform_skuKey"],
    })
    .then((result) => {
      const firstResult = extractFullMatch(result.hits);

      if (firstResult) {
        const searchURL = `/s/ecoventures`;
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set("destinations", firstResult.__platform_skuKey);

        if (data.dates[0]) {
          queryParams.set("from", data.dates[0].format("YYYY-MM-DD"));
        }
        if (data.guests !== "0") {
          queryParams.set("guests", data.guests);
        }

        const finalUrl = queryParams.toString()
          ? `${searchURL}?${queryParams.toString()}`
          : searchURL;

        return finalUrl;
      } else {
        throw new Error(`Destination ${where} not found`);
      }
    });
};

export const searchDestinationForLiveaboard = (
  where,
  destinationIndex,
  data
) => {
  return destinationIndex
    .search(where, {
      restrictSearchableAttributes: ["__platform_name"],
      attributesToRetrieve: ["__platform_url"],
    })
    .then((result) => {
      const firstResult = extractFullMatch(result.hits);

      if (firstResult) {
        const searchParams = new URLSearchParams(window.location.search);
        const searchURL = convertToSearchURL(
          firstResult.__platform_url,
          "liveaboard"
        );
        const queryParams = new URLSearchParams();

        if (data.dates[0]) {
          queryParams.set("from", data.dates[0].format("YYYY-MM-DD"));
        }

        if (data.guests !== "0") {
          queryParams.set("guests", data.guests);
        }

        if (searchParams.has("view")) {
          queryParams.set("view", searchParams.get("view"));
        }

        const finalUrl = queryParams.toString()
          ? `${searchURL}?${queryParams.toString()}`
          : searchURL;

        return finalUrl;
      } else {
        throw new Error(`Destination ${where} not found`);
      }
    });
};

export const searchDestinationForResort = (where, destinationIndex, data) => {
  return destinationIndex
    .search(where, {
      restrictSearchableAttributes: ["__platform_name"],
      attributesToRetrieve: ["__platform_url"],
    })
    .then((result) => {
      const firstResult = extractFullMatch(result.hits);

      if (firstResult) {
        const searchURL = convertToSearchURL(
          firstResult.__platform_url,
          "resort"
        );
        const queryParams = new URLSearchParams();

        if (data.dates[0]) {
          queryParams.set("from", data.dates[0].format("YYYY-MM-DD"));
        }
        if (data.dates[1]) {
          queryParams.set("to", data.dates[1].format("YYYY-MM-DD"));
        }
        if (data.guests !== "0") {
          queryParams.set("guests", data.guests);
        }

        const finalUrl = queryParams.toString()
          ? `${searchURL}?${queryParams.toString()}`
          : searchURL;

        return finalUrl;
      } else {
        throw new Error(`Destination ${where} not found`);
      }
    });
};

export const searchDestinationForYacht = (where, destinationIndex, data) => {
  return destinationIndex
    .search(where, {
      restrictSearchableAttributes: ["__platform_name"],
      attributesToRetrieve: ["__platform_url"],
    })
    .then((result) => {
      const firstResult = extractFullMatch(result.hits);

      if (firstResult) {
        const searchURL = convertToSearchURL(
          firstResult.__platform_url,
          "yacht"
        );
        const queryParams = new URLSearchParams();

        if (data.dates[0]) {
          queryParams.set("month", data.dates[0].format("YYYY-MMM"));
        }

        const finalUrl = queryParams.toString()
          ? `${searchURL}?${queryParams.toString()}`
          : searchURL;

        return finalUrl;
      } else {
        throw new Error(`Destination ${where} not found`);
      }
    });
};

export const convertToOtherSearchProduct = (sourceProduct, targetProduct) => {
  if (targetProduct === "ecoventure") {
    return `/s/ecoventures?destinations=${window.zubluDestination.code}`;
  }

  const url = new URL(window.location);
  return url.pathname.replace(sourceProduct, targetProduct);
};

export const getSupplierUrlWithParams = (supplierUrl, data) => {
  const queryParams = new URLSearchParams(window.location.search);

  if (data.dates[0]) {
    queryParams.set("from", data.dates[0].format("YYYY-MM-DD"));
  }

  if (data.dates[1]) {
    queryParams.set("to", data.dates[1].format("YYYY-MM-DD"));
  }

  if (data.guests !== "0") {
    queryParams.set("guests", data.guests);
  }

  return supplierUrl + "?" + queryParams.toString();
};

const countryNameDisplay = {
  Maldives: "the Maldives",
  Philippines: "the Philippines",
};

export const getLocation = (zubluDestination) => {
  let location = zubluDestination.name;
  let country = "";

  if (zubluDestination.country) {
    country = zubluDestination.country.name;
  } else if (zubluDestination.region) {
    country = zubluDestination.region.country.name;
  } else {
    country = zubluDestination.name;
  }

  if (zubluDestination.type === "country") {
    country = countryNameDisplay[country] || country;
  }

  if (!zubluDestination.country && !zubluDestination.region) {
    return country;
  }

  return `${location}, ${country}`;
};

export const getAlgoliaSuppliers = (supplierIds) => {
  const algolia = algoliasearchV4(
    window.zubluAlgolia.applicationID,
    window.zubluAlgolia.publicAPIKey
  );
  const resortIndex = algolia.initIndex(window.zubluAlgolia.resortIndex);

  return new Promise((resolve, reject) => {
    const queries = supplierIds.map((supplierId) => `objectID:${supplierId}`);
    resortIndex
      .search("", {
        facetFilters: [queries],
        hitsPerPage: 1000,
      })
      .then(resolve)
      .catch(reject);
  });
};

export const getBudgetsFilter = (type) => {
  const currency = window.topNavStore.state.currency;
  const searchParams = new URLSearchParams(window.location.search);

  let budgets = searchParams.get("budgets").split(",");
  const rangeInSelectedCurrency =
    currencyRanges[currency.code][type].filterRange;

  let results = [];
  const rangeInUSDKeys = Object.keys(rangeInSelectedCurrency);
  budgets.forEach((_b) => {
    for (let i = 0; i < rangeInUSDKeys.length; i++) {
      if (Object.values(rangeInSelectedCurrency)[i].includes(Number(_b))) {
        results.push(rangeInUSDKeys[i]);
      }
    }
  });

  return results.join(",");
};
